import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, Heart, Shield, Info, ShieldCheck, ChevronRight, KeyRound, Eye, EyeOff, Save } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { admin: 'Administrator', hauptmitarbeiter: 'Hauptmitarbeiter', mitarbeiter: 'Mitarbeiter' };

export default function SettingsPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [showPwForm, setShowPwForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) {
      toast.error('Alle Felder ausfüllen'); return;
    }
    if (pwForm.newPw !== pwForm.confirm) {
      toast.error('Neue Passwörter stimmen nicht überein'); return;
    }
    setSaving(true);
    try {
      await authAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Passwort erfolgreich geändert');
      setPwForm({ current: '', newPw: '', confirm: '' });
      setShowPwForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ändern');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 tracking-tight">Einstellungen</h1>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base">Profil</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-xl">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div>
              <p className="font-medium text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              <Badge variant="outline" className="text-xs mt-1 border-slate-200" data-testid="profile-role">
                {ROLE_LABELS[user?.role] || user?.role}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Passwort ändern */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-teal-700" />
              Passwort ändern
            </CardTitle>
            {!showPwForm && (
              <Button variant="outline" size="sm" onClick={() => setShowPwForm(true)} className="h-8 text-xs rounded-lg" data-testid="open-pw-change-btn">
                Ändern
              </Button>
            )}
          </div>
        </CardHeader>
        {showPwForm && (
          <CardContent className="space-y-3 animate-fade-in">
            <div className="space-y-1">
              <Label>Aktuelles Passwort</Label>
              <div className="relative">
                <Input type={showCurrent ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm(p => ({...p, current: e.target.value}))} placeholder="Aktuelles Passwort" className="h-11 pr-12" data-testid="current-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" onClick={() => setShowCurrent(!showCurrent)} tabIndex={-1}>
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Neues Passwort</Label>
              <div className="relative">
                <Input type={showNew ? 'text' : 'password'} value={pwForm.newPw} onChange={e => setPwForm(p => ({...p, newPw: e.target.value}))} placeholder="Min. 8 Zeichen, Aa1" className="h-11 pr-12" data-testid="new-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" onClick={() => setShowNew(!showNew)} tabIndex={-1}>
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Passwort wiederholen</Label>
              <div className="relative">
                <Input type={showConfirm ? 'text' : 'password'} value={pwForm.confirm} onChange={e => setPwForm(p => ({...p, confirm: e.target.value}))} placeholder="Neues Passwort wiederholen" className="h-11 pr-12" data-testid="confirm-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowPwForm(false); setPwForm({ current: '', newPw: '', confirm: '' }); }} className="flex-1 h-11 rounded-xl">Abbrechen</Button>
              <Button onClick={handleChangePassword} disabled={saving} className="flex-1 h-11 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="save-password-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? 'Speichern...' : 'Passwort speichern'}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {isAdmin && (
        <Card className="border border-violet-200 shadow-sm cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/admin')} data-testid="admin-link-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-violet-700" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900">Verwaltung</p>
              <p className="text-xs text-slate-500">Benutzer & Einstellungen verwalten</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300" />
          </CardContent>
        </Card>
      )}

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Info className="w-4 h-4 text-teal-700" />
            Über WundDok
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-600">
          <p>WundDok ist eine professionelle Web-App zur mobilen Wunddokumentation in der Pflege.</p>
          <p>Orientiert am DNQP Expertenstandard "Pflege von Menschen mit chronischen Wunden".</p>
          <div className="flex items-center gap-2 pt-2">
            <Heart className="w-4 h-4 text-teal-700" />
            <span className="text-xs text-slate-400">Version 2.0.0</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-teal-700" />
            Datenschutz
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600">
          <p>Diese Anwendung verarbeitet personenbezogene Daten gemäß der DSGVO.</p>
        </CardContent>
      </Card>

      <Button
        onClick={handleLogout}
        variant="outline"
        className="w-full h-12 text-rose-600 border-rose-200 hover:bg-rose-50 rounded-xl"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Abmelden
      </Button>
    </div>
  );
}
